import { useState, useEffect, useCallback, useRef } from 'react';
import {
  updateScene,
  listSceneVersions,
  insertSceneVersion,
  updateSceneVersion,
  startSceneVideo,
  pollSceneVideo,
  persistMedia,
  downloadMedia,
  SCENE_STATUS_META,
  type ContentScene,
  type ContentSceneVersion,
  type ContentProject,
} from '@/lib/contentStudio';

interface Props {
  scene: ContentScene;
  project: ContentProject;
  onChanged: () => void;
}

const POLL_MS = 8000;

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

/** Vrai si l'URL pointe déjà vers le stockage durable Zifek (et non une URL xAI temporaire). */
function isPermanentUrl(url: string): boolean {
  return url.includes('seaweedfs') || url.includes('product-media');
}

/**
 * Carte d'une scène : aperçu vidéo réel, génération / régénération via le
 * moteur vidéo xAI (Grok Imagine), suivi de la génération en direct, historique
 * des versions (l'ancienne vidéo n'est jamais supprimée) et bouton Télécharger.
 */
export default function SceneCard({ scene, project, onChanged }: Props) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [versions, setVersions] = useState<ContentSceneVersion[]>([]);
  const [visualPrompt, setVisualPrompt] = useState(scene.visual_prompt || '');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [persisting, setPersisting] = useState(false);
  const pollRef = useRef<number | null>(null);
  const resumeRef = useRef(false);

  const meta = SCENE_STATUS_META[scene.status];

  const refreshVersions = useCallback(async () => {
    const list = await listSceneVersions(scene.id);
    setVersions(list);
    return list;
  }, [scene.id]);

  useEffect(() => { refreshVersions(); }, [refreshVersions]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { window.clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const activeVersion = versions.find((v) => v.id === scene.active_version_id) || versions[0] || null;

  const startPolling = useCallback((versionId: number, requestId: string) => {
    stopPolling();
    const tick = async () => {
      try {
        const state = await pollSceneVideo(requestId);
        if (state.status === 'done' && state.videoUrl) {
          // On re-héberge la vidéo (URL xAI temporaire) vers le stockage durable.
          let finalUrl = state.videoUrl;
          try {
            finalUrl = await persistMedia(state.videoUrl, `scene-${pad(scene.number)}-v${versionId}.mp4`, 'content-studio/scenes', 'video/mp4');
          } catch { finalUrl = state.videoUrl; }
          await updateSceneVersion(versionId, { video_url: finalUrl, status: 'ready' });
          await updateScene(scene.id, { status: 'ready' });
          stopPolling();
          await refreshVersions();
          onChanged();
        } else if (state.status === 'failed' || state.status === 'expired') {
          await updateSceneVersion(versionId, { status: 'error', error: state.error || 'Échec de la génération' });
          await updateScene(scene.id, { status: 'error' });
          stopPolling();
          await refreshVersions();
          onChanged();
        }
      } catch (e) {
        await updateSceneVersion(versionId, { status: 'error', error: e instanceof Error ? e.message : 'Erreur de suivi' }).catch(() => undefined);
        await updateScene(scene.id, { status: 'error' }).catch(() => undefined);
        stopPolling();
        await refreshVersions();
        onChanged();
      }
    };
    pollRef.current = window.setInterval(tick, POLL_MS);
    tick();
  }, [scene.id, stopPolling, refreshVersions, onChanged]);

  // Reprise du suivi si une génération était déjà en cours.
  useEffect(() => {
    if (resumeRef.current) return;
    const processing = versions.find((v) => v.status === 'processing' && v.request_id);
    if (processing && processing.request_id) {
      resumeRef.current = true;
      startPolling(processing.id, processing.request_id);
    }
  }, [versions, startPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleSavePrompt = async () => {
    setBusy(true);
    try {
      await updateScene(scene.id, { visual_prompt: visualPrompt });
      setDirty(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateVideo = async () => {
    setBusy(true);
    setError(null);
    try {
      if (dirty) {
        await updateScene(scene.id, { visual_prompt: visualPrompt });
        setDirty(false);
      }
      const current = await listSceneVersions(scene.id);
      const nextVersion = (current[0]?.version || 0) + 1;

      // 1) Créer la nouvelle version (en traitement) — l'ancienne reste intacte.
      const v = await insertSceneVersion(scene.id, {
        version: nextVersion,
        prompt: visualPrompt,
        status: 'processing',
        media_kind: 'video',
      });
      if (!v) throw new Error('Impossible de créer la version de scène.');
      await updateScene(scene.id, { status: 'generating', active_version_id: v.id });
      await refreshVersions();
      onChanged();

      // 2) Démarrer la génération vidéo (image-to-video si une image existe).
      const { requestId } = await startSceneVideo({
        prompt: visualPrompt,
        duration: scene.duration,
        aspectRatio: project.aspect_ratio,
        resolution: '720p',
        image: activeVersion?.image_url || undefined,
      });
      await updateSceneVersion(v.id, { request_id: requestId, status: 'processing' });
      await refreshVersions();
      startPolling(v.id, requestId);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'La génération vidéo a échoué');
      await updateScene(scene.id, { status: 'error' }).catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };

  const handleToggleLock = async () => {
    setBusy(true);
    try {
      await updateScene(scene.id, { locked: !scene.locked });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const openVersions = async () => {
    if (!showVersions) await refreshVersions();
    setShowVersions((v) => !v);
  };

  const selectVersion = async (v: ContentSceneVersion) => {
    await updateScene(scene.id, { active_version_id: v.id, status: v.video_url ? 'ready' : scene.status });
    onChanged();
  };

  const handleDownload = async () => {
    if (!activeVersion?.video_url) return;
    setDownloading(true);
    try {
      await downloadMedia(activeVersion.video_url, `scene-${pad(scene.number)}-v${activeVersion.version}.mp4`);
    } finally {
      setDownloading(false);
    }
  };

  const handlePersist = async () => {
    if (!activeVersion?.video_url) return;
    setPersisting(true);
    setError(null);
    try {
      const permanent = await persistMedia(
        activeVersion.video_url,
        `scene-${pad(scene.number)}-v${activeVersion.version}.mp4`,
        'content-studio/scenes',
        'video/mp4',
      );
      await updateSceneVersion(activeVersion.id, { video_url: permanent });
      await refreshVersions();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de rendre le lien permanent');
    } finally {
      setPersisting(false);
    }
  };

  const processing = activeVersion?.status === 'processing';

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden flex flex-col">
      {/* En-tête */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-background-200/70">
        <span className="text-xs font-bold text-foreground-500 tracking-wider">SCÈNE {pad(scene.number)}</span>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${meta.className}`}>
          <i className={meta.icon}></i>{meta.label}
        </span>
        <span className="ml-auto flex items-center gap-2">
          <span className="text-[11px] text-foreground-400 inline-flex items-center gap-1">
            <i className="ri-timer-line"></i>{scene.duration}s
          </span>
          <button
            type="button"
            onClick={handleToggleLock}
            disabled={busy}
            title={scene.locked ? 'Scène verrouillée (non régénérée en global)' : 'Verrouiller la scène'}
            className={`w-6 h-6 flex items-center justify-center rounded-md transition-colors cursor-pointer ${
              scene.locked ? 'bg-secondary-100 text-secondary-800' : 'text-foreground-400 hover:bg-background-100'
            }`}
          >
            <i className={`${scene.locked ? 'ri-lock-2-fill' : 'ri-lock-2-line'} text-sm`}></i>
          </button>
        </span>
      </div>

      {/* Aperçu vidéo */}
      <div className="relative aspect-video bg-black/90 flex items-center justify-center overflow-hidden">
        {processing && (
          <span className="absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-[10px] font-bold shadow-sm">
            <i className="ri-loader-4-line animate-spin"></i>
            Scène en génération…
          </span>
        )}
        {activeVersion?.video_url ? (
          <video src={activeVersion.video_url} controls playsInline className="w-full h-full object-contain bg-black" />
        ) : processing ? (
          <div className="flex flex-col items-center text-center px-4">
            <i className="ri-loader-4-line animate-spin text-2xl text-accent-400 mb-1"></i>
            <span className="text-[11px] font-medium text-white/80">Génération vidéo en cours…</span>
            <span className="text-[10px] text-white/50 mt-0.5">La scène apparaîtra dès qu'elle est prête.</span>
          </div>
        ) : activeVersion?.status === 'error' ? (
          <div className="flex flex-col items-center text-center px-4">
            <i className="ri-error-warning-line text-2xl text-red-400 mb-1"></i>
            <span className="text-[11px] text-white/70">Échec de la génération</span>
          </div>
        ) : (
          <div className="flex flex-col items-center text-center px-4">
            <i className="ri-film-line text-2xl text-white/40 mb-1"></i>
            <span className="text-[11px] text-white/50">Aucune vidéo générée</span>
          </div>
        )}
      </div>

      {/* Corps */}
      <div className="p-3 flex-1 flex flex-col">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1.5 text-xs font-semibold text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer mb-2"
        >
          <i className={`${expanded ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'}`}></i>
          Script de la scène
        </button>
        <p className={`text-xs text-foreground-600 leading-relaxed ${expanded ? '' : 'line-clamp-3'}`}>
          {scene.script || '—'}
        </p>

        <div className="mt-3">
          <label className="block text-[11px] font-medium text-foreground-500 mb-1">Prompt visuel (moteur vidéo)</label>
          <textarea
            value={visualPrompt}
            onChange={(e) => { setVisualPrompt(e.target.value); setDirty(true); }}
            rows={3}
            className="w-full px-2.5 py-2 bg-background-50 border border-background-200/70 rounded-md text-xs text-foreground-800 focus:outline-none focus:border-accent-300 resize-none"
          />
        </div>

        {error && (
          <p className="mt-2 text-[11px] text-red-600 bg-red-50 px-2 py-1.5 rounded-md flex items-start gap-1.5">
            <i className="ri-error-warning-line mt-0.5"></i>{error}
          </p>
        )}
        {activeVersion?.status === 'error' && activeVersion.error && !error && (
          <p className="mt-2 text-[11px] text-red-600 bg-red-50 px-2 py-1.5 rounded-md flex items-start gap-1.5">
            <i className="ri-error-warning-line mt-0.5"></i>{activeVersion.error}
          </p>
        )}

        <div className="flex items-center gap-2 mt-3">
          <button
            type="button"
            onClick={handleGenerateVideo}
            disabled={busy || processing}
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 disabled:opacity-60 transition-colors cursor-pointer"
          >
            {busy || processing ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-video-add-line"></i>}
            {processing ? 'Génération…' : activeVersion?.video_url ? 'Régénérer la vidéo' : 'Générer la vidéo'}
          </button>
          {dirty && (
            <button
              type="button"
              onClick={handleSavePrompt}
              disabled={busy}
              title="Enregistrer le prompt"
              className="w-9 h-9 flex items-center justify-center rounded-full border border-background-200/70 text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-save-line text-sm"></i>
            </button>
          )}
          <button
            type="button"
            onClick={openVersions}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-history-line"></i>Versions
          </button>
        </div>

        {activeVersion?.video_url && (
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-accent-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-accent-600 disabled:opacity-60 transition-colors cursor-pointer"
            >
              {downloading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-download-2-line"></i>}
              Télécharger la vidéo (.mp4)
            </button>
            {!isPermanentUrl(activeVersion.video_url) && (
              <button
                type="button"
                onClick={handlePersist}
                disabled={persisting}
                title="Re-héberger la vidéo pour un lien permanent"
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border border-background-200/70 text-xs font-medium text-foreground-600 whitespace-nowrap hover:bg-background-100 disabled:opacity-60 transition-colors cursor-pointer"
              >
                {persisting ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-link-m"></i>}
                Lien permanent
              </button>
            )}
          </div>
        )}

        {showVersions && (
          <div className="mt-3 border-t border-background-200/70 pt-2">
            {versions.length === 0 ? (
              <p className="text-[11px] text-foreground-400 py-1">Aucune version pour l'instant.</p>
            ) : (
              <div className="space-y-1.5">
                {versions.map((v) => (
                  <div key={v.id} className="flex items-center gap-2 text-[11px]">
                    <span className={`w-6 h-6 flex items-center justify-center rounded-md ${v.id === scene.active_version_id ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'}`}>
                      v{v.version}
                    </span>
                    <span className="text-foreground-500 flex-1 truncate">
                      {v.status === 'processing' ? 'En cours…' : v.status === 'error' ? 'Échec' : v.video_url ? 'Vidéo prête' : 'Plan'}
                    </span>
                    {v.video_url && (
                      <button
                        type="button"
                        onClick={() => downloadMedia(v.video_url as string, `scene-${pad(scene.number)}-v${v.version}.mp4`)}
                        title="Télécharger cette version"
                        className="w-6 h-6 flex items-center justify-center rounded-md text-foreground-400 hover:text-accent-600 hover:bg-accent-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-download-2-line"></i>
                      </button>
                    )}
                    {v.id === scene.active_version_id ? (
                      <span className="text-accent-700 font-semibold whitespace-nowrap">Active</span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => selectVersion(v)}
                        className="text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                      >
                        Utiliser
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}